import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import Absence from '../../components/tables/Absence.jsx';
import { getAllEmployees } from '../../services/EmployeeService';
import PageHeader from '../../components/headers/Header';
import AbsenceDetailsModal from '../../components/DViews/AbsenceDetailsModal';
import AbsenceDashboard from '../../components/dash/AbsenceDash';
import DayDetailsModal from '../../components/DViews/CDayDetails.jsx';
import JustificationUploadModal from '../../components/DViews/JUModal.jsx';
import ElegantAddButton from '../../components/btns/AddButton.jsx';
import AbsenceDetailsDialog from '../../components/DViews/StoringAbsenceDM.jsx';
import {
  getAllAbsences,
  getMyAbsences,
  getAllStagedAbsences,
  getAbsencesByTypeAndMonth
} from '../../services/absenceAPIs';
import '../../components/leaves/Leaves.css';

const isEmployee = localStorage.getItem('userRole') === 'EMPLOYEE';

const Personal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Refs
  const absencesRef = useRef(null);

  // State
  const [selectedToStore, setSelectedToStore] = useState(null);
  const [stagedAbsences, setStagedAbsences] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [personalAbsences, setPersonalAbsences] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [activeTab, setActiveTab] = useState(isEmployee ? 'add' : 'overview');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [dayAbsences, setDayAbsences] = useState([]);
  const [showDayDetailsModal, setShowDayDetailsModal] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    department: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    search: '',
    includeLateness: true
  });

  const isLargeScreen = useMediaQuery('(min-width: 1600px)');

  // Navigation state effect
  useEffect(() => {
    const state = location.state || {};

    if (state.targetTab) {
      setActiveTab(state.targetTab);
    }

    if (state.filters) {
      setFilters(prev => ({
        ...prev,
        year: state.filters.year || prev.year,
        month: state.filters.month || prev.month,
        office: state.filters.office === 'all' ? '' : state.filters.office || prev.office,
      }));
    }
  }, [location.state]);

  // Handle tab switching for employee/non-employee
  useEffect(() => {
    if (isEmployee && activeTab === 'overview') {
      setActiveTab('add');
    } else if (!isEmployee && activeTab === 'add') {
      setActiveTab('overview');
    }
  }, [isEmployee, activeTab]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const allEmployees = await getAllEmployees();
        setEmployees(allEmployees);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch absences
  const fetchAbsences = useCallback(async () => {
    try {
      let data = [];
      const stagedAbsencesData = await getAllStagedAbsences();

      if (isEmployee) {
        data = await getMyAbsences();
      } else {
        data = await getAbsencesByTypeAndMonth('PERSONAL', filters.year, filters.month);
      }

      setAbsences(data);
      setStagedAbsences(stagedAbsencesData);
      setPersonalAbsences(data.filter(abs => abs.ty_pe === 'PERSONAL'));
    } catch (error) {
      console.error('Error fetching absences:', error);
    }
  }, [isEmployee, filters.year, filters.month]);

  // Initial fetch
  useEffect(() => {
    fetchAbsences();
  }, [fetchAbsences]);

  // Handlers
  const handleEmployeeSelect = (id) => {
    setSelectedEmployeeId(id);
    console.log('Selected employee ID:', id);
  };

  const handleCalendarDateClick = (date, absencesForDay) => {
    setSelectedCalendarDate(date);
    setDayAbsences(absencesForDay);
    setShowDayDetailsModal(true);
  };

  const handleNavigateToUnconfirmed = () => {
    navigate('/unconfirmed', { state: { absenceType: 'PERSONAL' } });
  };

  return (
    <div>
      <PageHeader
        title="Personal Commitment"
        subtitle="Manage and track personal commitments"
        actions={
          <>
            <ElegantAddButton
              text="Add Personal"
              onClick={() => navigate('/personal/add')}
            />
            <ElegantAddButton
              text="Confirm Personal Commitment"
              onClick={handleNavigateToUnconfirmed}
            />
          </>
        }
      />

      <AbsenceDashboard IMGemployees={employees} perso={true} />

      <div id="absences-container" ref={absencesRef}>
        <Absence
          filters={filters}
          setFilters={setFilters}
          setSelectedAbsence={setSelectedAbsence}
          setSelectToStore={setSelectedToStore}
          absences={personalAbsences}
          setSelectedUpload={setSelectedUpload}
          unconfirmed={false}
          fetchAbsences={fetchAbsences}
          IsEmployee={isEmployee}
          perso={true}
        />
      </div>

      {/* Modals */}
      {selectedAbsence && (
        <AbsenceDetailsModal
          selectedAbsence={selectedAbsence}
          setSelectedAbsence={setSelectedAbsence}
          fetchAbsences={fetchAbsences}
          IsEmployee={isEmployee}
        />
      )}

      {selectedUpload && (
        <JustificationUploadModal
          selectedUpload={selectedUpload}
          setSelectedUpload={setSelectedUpload}
          fetchAbsences={fetchAbsences}
        />
      )}

      <DayDetailsModal
        open={showDayDetailsModal}
        onClose={() => setShowDayDetailsModal(false)}
        selectedDate={selectedCalendarDate}
        dayAbsences={dayAbsences}
        employees={employees}
      />

      {selectedToStore && (
        <AbsenceDetailsDialog
          open={Boolean(selectedToStore)}
          onClose={() => setSelectedToStore(null)}
          absence={selectedToStore}
          setSelectToStore={setSelectedToStore}
          fetchAbsences={fetchAbsences}
        />
      )}
    </div>
  );
};

export default Personal;
